/**
 * Normalises the required-documents fields from the backend into a consistent shape.
 *
 * The backend returns several different field names and formats for required docs
 * (array of strings, array of objects, comma-separated string). This module
 * reduces all of them to a uniform NormalisedDoc[].
 */
import { getDocLabel } from './format-utils';
import { BACKEND_DOC_TYPES } from '../constants';

export interface NormalisedDoc {
  document_type: string;
  label:         string;
  required:      boolean;
}

/**
 * Strip wrapper punctuation that leaks in from the AI stream.
 *
 * The agent-predicted list is free-form, (often double-) JSON-encoded text, so a
 * single item can arrive as `"Salary Slips"`, `["Investment Proof"`, `Any Other"]`
 * or `\"Tax Computation Sheet\"`. We peel any surrounding quotes / brackets /
 * backslashes / whitespace so only the human-readable name survives. Anything
 * that cleans down to nothing is dropped by the caller.
 */
function cleanDocToken(value: string): string {
  let prev = '';
  let out  = value;
  // Loop: a single item can be wrapped more than once (e.g. `["\"x\""]`).
  while (out !== prev) {
    prev = out;
    out  = out.trim().replace(/^[\s"'`[\]{}\\]+|[\s"'`[\]{}\\]+$/g, '');
  }
  return out;
}

/**
 * Reduce the raw backend value to a flat list of items.
 *
 * `merged_required_documents` already arrives as an array of objects, but
 * `required_documents_from_customer_predicted_by_agent1` is a JSON-encoded
 * string (sometimes double-encoded, sometimes malformed). We unwrap up to 3
 * JSON layers; if that fails we fall back to a comma split. Either way every
 * item is cleaned by the caller, so malformed JSON never reaches the UI verbatim.
 */
function resolveItems(raw: unknown): unknown[] {
  if (Array.isArray(raw)) return raw;
  if (typeof raw !== 'string' || !raw.trim()) return [];

  let value: unknown = raw.trim();
  for (let i = 0; i < 3 && typeof value === 'string'; i++) {
    const s = value.trim();
    if (!/^[[{"]/.test(s)) break;          // not JSON-shaped — stop unwrapping
    try { value = JSON.parse(s); } catch { break; }
  }

  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') return [value];
  return (typeof value === 'string' ? value : raw).split(',');
}

/**
 * Convert `required_documents_from_customer_predicted_by_agent1`
 * or `merged_required_documents` into a uniform array.
 * Internal (ERI-sourced) doc types are filtered out.
 */
export function normaliseRequiredDocs(
  raw:           unknown,
  internalTypes: Set<string>,
): NormalisedDoc[] {
  return resolveItems(raw)
    .map(item => {
      if (typeof item === 'string') {
        const dt = cleanDocToken(item).toLowerCase();
        return { document_type: dt, label: getDocLabel(dt), required: true };
      }
      if (item && typeof item === 'object') {
        const obj   = item as Record<string, unknown>;
        const dt    = cleanDocToken(String(obj.document_type ?? obj.type ?? '')).toLowerCase();
        const label = cleanDocToken(String(obj.label ?? ''));
        return {
          document_type: dt,
          label:         getDocLabel(dt, label || undefined),
          required:      obj.required !== false,
        };
      }
      return null;
    })
    .filter(
      (d): d is NormalisedDoc =>
        d !== null && !!d.document_type && !internalTypes.has(d.document_type),
    );
}

/**
 * Splits a string into segments, highlighting rupee amounts and percentages.
 * Returns an array of plain-string parts (alternate segments are the matches).
 */
export function highlightRupees(text: string): string[] {
  return text.split(/(₹\s*[\d,]+(?:\.\d+)?|[\d,]+(?:\.\d+)?%?)/g);
}

const slugify = (s: string | null | undefined) =>
  (s ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');

/**
 * What to send to initiate-upload for a requested slot. Slots the backend knows
 * keep their type; custom ones (admin-typed, e.g. "Msme") go up as `other` with
 * the slot's own label, which is how the slot is recognised again afterwards.
 */
export function toUploadTarget(
  slotType: string,
  slotLabel: string | undefined,
  fallbackLabel?: string,
): { documentType: string; documentLabel?: string } {
  if (BACKEND_DOC_TYPES.has(slotType)) {
    return { documentType: slotType, documentLabel: fallbackLabel };
  }
  return { documentType: 'other', documentLabel: slotLabel || getDocLabel(slotType) };
}

/** True if an already-uploaded document fills the given requested slot. */
export function docFillsSlot(
  doc: { document_type: string; document_label?: string | null },
  slot: { document_type: string; label: string },
): boolean {
  if (BACKEND_DOC_TYPES.has(slot.document_type)) return doc.document_type === slot.document_type;
  return doc.document_type === 'other' && slugify(doc.document_label) === slugify(slot.label);
}
